import { ICONS } from '@/constants/constants';
import Image from 'next/image';
import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { getCanadaTorontoNow, parseDDMMYYYY, formatDDMMYYYY } from '@/utils/dateTime'

interface TripCalendarProps {
    onDateSelect: (date: string) => void;
    onClose: () => void;
    selectedDate?: string;
    triggerElement: HTMLElement | null;
    minDate?: string; // Minimum selectable date in DD/MM/YYYY format
}

const TripCalendar = ({ onDateSelect, onClose, selectedDate, triggerElement, minDate }: TripCalendarProps) => {
    // Use Canadian timezone for all date operations
    const canadaToday = getCanadaTorontoNow()

    const today = new Date(canadaToday.year, canadaToday.monthIndex, canadaToday.day)
    const currentRealMonth = new Date(canadaToday.year, canadaToday.monthIndex)
    const [currentMonth, setCurrentMonth] = useState(currentRealMonth)
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const [isPositioned, setIsPositioned] = useState(false)
    const calendarRef = useRef<HTMLDivElement>(null)

    // Calculate position based on trigger element
    useEffect(() => {
        if (triggerElement && triggerElement.getBoundingClientRect) {
            // Small delay to ensure smooth positioning
            requestAnimationFrame(() => {
                const rect = triggerElement.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
                
                // Get viewport dimensions
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                
                // Calendar dimensions (responsive based on viewport)
                const isMobile = viewportWidth < 768
                const calendarWidth = isMobile 
                    ? Math.min(280, viewportWidth - 24) // Smaller on mobile
                    : Math.min(320, viewportWidth - 32) // Larger on tablet/desktop
                const calendarHeight = 400 // approximate height
                
                // Initial position
                let top = rect.bottom + scrollTop + 4
                let left = rect.left + scrollLeft
                
                // Adjust horizontal position if calendar would go outside viewport
                if (left + calendarWidth > viewportWidth + scrollLeft) {
                    // Try to align to the right edge of trigger element
                    left = rect.right + scrollLeft - calendarWidth
                    
                    // If still outside, align to viewport edge with some padding
                    if (left < scrollLeft + 16) {
                        left = scrollLeft + 16 // 16px padding from left edge
                    }
                }
                
                // Adjust vertical position if calendar would go outside viewport
                if (top + calendarHeight > viewportHeight + scrollTop) {
                    // Position above the trigger element instead
                    top = rect.top + scrollTop - calendarHeight - 4
                    
                    // If positioning above would also go outside viewport, keep it below but adjust
                    if (top < scrollTop + 16) {
                        top = rect.bottom + scrollTop + 4 // Keep below but will be constrained by final boundary check
                    }
                }
                
                // Final boundary checks to ensure calendar stays within viewport
                const maxLeft = viewportWidth + scrollLeft - calendarWidth - 16
                const maxTop = viewportHeight + scrollTop - calendarHeight - 16
                
                left = Math.max(scrollLeft + 16, Math.min(left, maxLeft))
                top = Math.max(scrollTop + 16, Math.min(top, maxTop))

                setPosition({
                    top: top,
                    left: left
                })
                setIsPositioned(true)
            })
        }
    }, [triggerElement])

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && event.target && !calendarRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ]

    const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const startingDayOfWeek = firstDay.getDay()

        const days = []

        // Get previous month's last days to fill empty cells at the beginning
        const prevMonth = new Date(year, month - 1, 0)
        const prevMonthDays = prevMonth.getDate()

        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                day: prevMonthDays - i,
                isCurrentMonth: false,
                isPrevMonth: true,
                isNextMonth: false
            })
        }

        // Add days of the current month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push({
                day: day,
                isCurrentMonth: true,
                isPrevMonth: false,
                isNextMonth: false
            })
        }

        // Fill remaining cells with next month's days
        const totalCells = 42 // 6 rows × 7 days
        const remainingCells = totalCells - days.length

        for (let day = 1; day <= remainingCells; day++) {
            days.push({
                day: day,
                isCurrentMonth: false,
                isPrevMonth: false,
                isNextMonth: true
            })
        }

        return days
    }

    const handleDateClick = (dayObj: any) => {
        // Only allow clicking on current month dates
        if (!dayObj.isCurrentMonth) {
            return
        }

       
        // Check if the date is in the past or before minDate using Canadian timezone
        const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), dayObj.day)
        const todayStart = new Date(canadaToday.year, canadaToday.monthIndex, canadaToday.day)

        // If minDate is provided, use it as the minimum selectable date
        if (minDate) {
            const minDateParts = parseDDMMYYYY(minDate)
            if (minDateParts) {
                const minDateObj = new Date(minDateParts.year, minDateParts.monthIndex, minDateParts.day)
                if (clickedDate < minDateObj) {
                    return // Don't allow selection of dates before minDate
                }
            } else {
                // Fallback to today check if minDate parsing fails
                if (clickedDate < todayStart) {
                    return
                }
            }
        } else if (clickedDate < todayStart) {
            return // Don't allow selection of past dates
        }

        const selectedDate = formatDDMMYYYY(currentMonth.getFullYear(), currentMonth.getMonth(), dayObj.day)

        onDateSelect(selectedDate)
        onClose()
    }

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))
    }

    const prevMonth = () => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
        // Only allow going back if the new month is not before the current real month
        if (newMonth >= currentRealMonth) {
            setCurrentMonth(newMonth)
        }
    }

    // Check if we can go to previous month (current displayed month is after real current month)
    const canGoPrevious = currentMonth > currentRealMonth

    // Parse the selected date to get the day number
    const getSelectedDay = () => {
        if (!selectedDate) return null

        const selectedDateParts = parseDDMMYYYY(selectedDate)
        if (!selectedDateParts) return null

        // Check if the selected date is in the current month being displayed
        if (selectedDateParts.monthIndex === currentMonth.getMonth() &&
            selectedDateParts.year === currentMonth.getFullYear()) {
            return selectedDateParts.day
        }

        return null
    }

    const selectedDay = getSelectedDay()
    const days = getDaysInMonth(currentMonth)

    // Helper function to check if a date is in the past or before minDate using Canadian timezone
    const isPastDate = (dayObj: any) => {
        let dateToCheck
        if (dayObj.isPrevMonth) {
            dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, dayObj.day)
        } else if (dayObj.isNextMonth) {
            dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, dayObj.day)
        } else {
            dateToCheck = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), dayObj.day)
        }

        const todayStart = new Date(canadaToday.year, canadaToday.monthIndex, canadaToday.day)
        
        // If minDate is provided, use it as the minimum selectable date
        if (minDate) {
            const minDateParts = parseDDMMYYYY(minDate)
            if (minDateParts) {
                const minDateObj = new Date(minDateParts.year, minDateParts.monthIndex, minDateParts.day)
                return dateToCheck < minDateObj
            } else {
                return dateToCheck < todayStart
            }
        }
        
        return dateToCheck < todayStart
    }

    const calendarContent = isPositioned ? (
        <div
            ref={calendarRef}
            className="absolute bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] p-4 w-70 md:w-80 max-w-[calc(100vw-24px)]"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >

            <div className="flex items-center justify-between  mb-4">
                <div className='flex'>
                    {canGoPrevious && (
                        <button
                            onClick={prevMonth}
                            className="p-1 cursor-pointer  rounded"
                        >
                            <Image src={ICONS.leftArrowv} alt='leftArrow' height={10} width={10} />

                        </button>
                    )}

                    <h3 className="text-lg font-semibold text-gray-800">
                        {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                </div>

                <button
                    onClick={nextMonth}
                    className="p-1  rounded"
                >
                    <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.70615 6.29414C8.09678 6.68477 8.09678 7.31914 7.70615 7.70977L1.70615 13.7098C1.31553 14.1004 0.681152 14.1004 0.290527 13.7098C-0.100098 13.3191 -0.100098 12.6848 0.290527 12.2941L5.58428 7.00039L0.293652 1.70664C-0.0969727 1.31602 -0.0969727 0.681641 0.293652 0.291016C0.684277 -0.0996094 1.31865 -0.0996094 1.70928 0.291016L7.70928 6.29102L7.70615 6.29414Z" fill="#9CA3AF" />
                    </svg>

                </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((dayObj, index) => {
                    const isSelected = dayObj.isCurrentMonth && dayObj.day === selectedDay
                    const isPast = isPastDate(dayObj)
                    const isDisabled = !dayObj.isCurrentMonth || isPast

                    return (
                        <div key={index} className="aspect-square">
                            <button
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (!isDisabled) {
                                        handleDateClick(dayObj)
                                    }
                                }}
                                disabled={isDisabled}
                                className={`px-4 py-1 flex items-center justify-center text-sm rounded-md transition-colors ${isSelected
                                    ? 'bg-gray-900 text-white'
                                    : isDisabled
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-700 cursor-pointer'
                                    }`}
                            >
                                {dayObj.day}
                            </button>
                        </div>
                    )
                })}
            </div>
        </div>
    ) : null

    return typeof window !== 'undefined' && calendarContent
        ? createPortal(calendarContent, document.body)
        : null
}

export default TripCalendar